import { Flag, Route, ClipboardList, Ruler, Clock, Activity } from 'lucide-react';
import { useState } from 'react';
import RowExportMenu from './RowExportMenu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { fmtClock } from '@/components/PiecesUI';
import { formatSplitTenths } from '@/lib/strokeSplit';

/**
 * SessionBreakdown — renders parent → child session strips inside an
 * expanded History card, per CrewZ Session Data Model v1.6 §7.
 *
 * Legacy rows (no `child_sessions[]`) are collapsed into a single
 * synthetic child derived from the row's fields:
 *   - `splits_500m` → race child
 *   - `program_name` → program child
 *   - `target_distance` → distance child
 *   - otherwise standard child (skipped — the main analysis chart
 *     already covers this)
 *
 * Slice C adds a one-tap export button on every child strip. Because
 * v1.6 child sessions don't carry their own time-series arrays yet,
 * every export re-uses the PARENT workout — the user gets a full-session
 * TCX in one tap regardless of which strip they clicked.
 */
function ChildHeader({ icon: Icon, label, tone, extras, exportBtn, testid }) {
  return (
    <div className="flex items-center gap-2 mb-2">
      <Icon className={`w-4 h-4 ${tone}`} />
      <span className={`text-xs font-bold uppercase tracking-widest ${tone}`}>{label}</span>
      {extras}
      {exportBtn && (
        <div className="ml-auto" data-testid={testid}>
          {exportBtn}
        </div>
      )}
    </div>
  );
}

function KV({ label, value, testid }) {
  return (
    <div className="flex flex-col">
      <span className="text-[10px] font-bold uppercase tracking-widest text-zinc-400">{label}</span>
      <span
        className="text-sm font-bold text-zinc-800 dark:text-zinc-100"
        style={{ fontFamily: 'JetBrains Mono, monospace' }}
        data-testid={testid}
      >
        {value ?? '—'}
      </span>
    </div>
  );
}

function RaceChild({ workout, formatters, exportBtn }) {
  const splits = workout.splits_500m || [];
  return (
    <div className="rounded-lg border-2 border-amber-200 dark:border-amber-900/40 bg-amber-50/60 dark:bg-amber-950/20 p-3" data-testid="child-race">
      <ChildHeader
        icon={Flag}
        label="Race Piece"
        tone="text-amber-700 dark:text-amber-300"
        exportBtn={exportBtn}
        testid="child-race-export-slot"
      />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
        <KV label="Target" value={formatters.formatDistanceMeters(workout.target_distance || workout.distance)} testid="race-target" />
        <KV label="Total Time" value={formatters.formatDurationFull(workout.moving_time_s ?? workout.duration)} testid="race-time" />
        <KV label="Avg Split" value={formatters.formatSplit(workout.avg_split)} testid="race-split" />
        <KV label="Avg HR" value={workout.avg_heart_rate ? `${workout.avg_heart_rate} bpm` : null} testid="race-hr" />
      </div>
      {splits.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="race-splits-table">
            <thead>
              <tr className="text-zinc-400 uppercase tracking-widest">
                <th className="text-left font-bold py-1">Split</th>
                <th className="text-right font-bold py-1">Time</th>
                <th className="text-right font-bold py-1">Pace/500m</th>
              </tr>
            </thead>
            <tbody className="font-mono">
              {splits.map((s, i) => (
                <tr key={i} className="border-t border-amber-100 dark:border-amber-900/30">
                  <td className="py-1">{s.boundary_m ? `${s.boundary_m}m` : `#${i + 1}`}</td>
                  <td className="text-right py-1">{formatters.formatDurationFull(s.split_ms ? Math.round(s.split_ms / 1000) : s.time)}</td>
                  <td className="text-right py-1">{formatters.formatSplit(s.pace_500m ?? (s.split_ms ? s.split_ms / 1000 : 0))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ProgramChild({ workout, formatters, exportBtn }) {
  const pieces = workout.pieces || workout.program_pieces || [];
  return (
    <div className="rounded-lg border-2 border-green-200 dark:border-green-900/40 bg-green-50/60 dark:bg-green-950/20 p-3" data-testid="child-program">
      <ChildHeader
        icon={ClipboardList}
        label="Program"
        tone="text-green-700 dark:text-green-300"
        extras={workout.program_name && (
          <span className="text-xs font-bold text-green-800 dark:text-green-200 truncate max-w-[12rem]">
            {workout.program_name}
          </span>
        )}
        exportBtn={exportBtn}
        testid="child-program-export-slot"
      />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KV label="Distance" value={formatters.formatDistanceMeters(workout.distance)} testid="program-distance" />
        <KV label="Moving Time" value={formatters.formatDurationFull(workout.moving_time_s ?? workout.duration)} testid="program-time" />
        <KV label="Avg Split" value={formatters.formatSplit(workout.avg_split)} testid="program-split" />
        <KV label="Avg HR" value={workout.avg_heart_rate ? `${workout.avg_heart_rate} bpm` : null} testid="program-hr" />
      </div>
      {pieces.length > 0 && (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-xs" data-testid="program-pieces-table">
            <thead>
              <tr className="text-zinc-400 uppercase tracking-widest">
                <th className="text-left font-bold py-1">Piece</th>
                <th className="text-right font-bold py-1">Target</th>
                <th className="text-right font-bold py-1">Actual</th>
                <th className="text-right font-bold py-1">Split</th>
              </tr>
            </thead>
            <tbody className="font-mono">
              {pieces.map((p, i) => (
                <tr key={i} className="border-t border-green-100 dark:border-green-900/30">
                  <td className="py-1">{p.name || `#${i + 1}`}</td>
                  <td className="text-right py-1">{p.target_distance ? formatters.formatDistanceMeters(p.target_distance) : (p.target_time || '—')}</td>
                  <td className="text-right py-1">{p.actual_distance ? formatters.formatDistanceMeters(p.actual_distance) : (p.actual_time || '—')}</td>
                  <td className="text-right py-1">{formatters.formatSplit(p.avg_split)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function DistanceChild({ workout, formatters, exportBtn }) {
  return (
    <div className="rounded-lg border-2 border-blue-200 dark:border-blue-900/40 bg-blue-50/60 dark:bg-blue-950/20 p-3" data-testid="child-distance">
      <ChildHeader
        icon={Route}
        label="Distance Target"
        tone="text-blue-700 dark:text-blue-300"
        exportBtn={exportBtn}
        testid="child-distance-export-slot"
      />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KV label="Target" value={formatters.formatDistanceMeters(workout.target_distance)} testid="distance-target" />
        <KV label="Finish" value={formatters.formatDurationFull(workout.moving_time_s ?? workout.duration)} testid="distance-finish" />
        <KV label="Avg Split" value={formatters.formatSplit(workout.avg_split)} testid="distance-split" />
        <KV label="Avg HR" value={workout.avg_heart_rate ? `${workout.avg_heart_rate} bpm` : null} testid="distance-hr" />
      </div>
    </div>
  );
}

function StandardChild({ workout, formatters, exportBtn }) {
  return (
    <div className="rounded-lg border-2 border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800/40 p-3" data-testid="child-standard">
      <ChildHeader
        icon={Activity}
        label="Standard Session"
        tone="text-zinc-500 dark:text-zinc-400"
        exportBtn={exportBtn}
        testid="child-standard-export-slot"
      />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KV label="Distance" value={formatters.formatDistanceMeters(workout.distance)} testid="std-distance" />
        <KV label="Moving Time" value={formatters.formatDurationFull(workout.moving_time_s ?? workout.duration)} testid="std-time" />
        <KV label="Avg Split" value={formatters.formatSplit(workout.avg_split)} testid="std-split" />
        <KV label="Avg HR" value={workout.avg_heart_rate ? `${workout.avg_heart_rate} bpm` : null} testid="std-hr" />
      </div>
    </div>
  );
}

// A piece run inside the session — tap opens its own summary.
function PieceChild({ piece }) {
  const [open, setOpen] = useState(false);
  const n = piece.piece_number;
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full text-left rounded-lg border-2 border-[#1565C0]/30 dark:border-[#4A9EEB]/30 bg-blue-50/60 dark:bg-blue-950/20 p-3 hover:border-[#1565C0] dark:hover:border-[#4A9EEB] transition-colors"
        data-testid={`child-piece-${n}`}
      >
        <ChildHeader
          icon={Flag}
          label={`Piece ${n} · ${piece.piece_name || piece.piece_type}`}
          tone="text-[#1565C0] dark:text-[#4A9EEB]"
          extras={<span className="text-[10px] text-zinc-400 ml-1">{fmtClock(piece.start_offset_s)} → {fmtClock(piece.end_offset_s)}</span>}
        />
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
          <KV label="Distance" value={`${piece.distance_m ?? 0} m`} testid={`child-piece-${n}-distance`} />
          <KV label="Time" value={fmtClock(piece.time_s)} testid={`child-piece-${n}-time`} />
          <KV label="Avg split" value={formatSplitTenths(piece.avg_split_s)} />
          <KV label="Avg rate" value={piece.avg_stroke_rate ?? '—'} />
          <KV label="Strokes" value={piece.stroke_count ?? '—'} />
        </div>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md bg-white dark:bg-zinc-900 text-zinc-900 dark:text-zinc-100 border border-zinc-200 dark:border-zinc-700" data-testid={`piece-summary-dialog-${n}`}>
          <DialogHeader>
            <DialogTitle className="text-[#1565C0] dark:text-[#4A9EEB]">Piece {n} · {piece.piece_name}</DialogTitle>
            <DialogDescription className="text-zinc-600 dark:text-zinc-300">
              {piece.piece_type} piece · session window {fmtClock(piece.start_offset_s)} → {fmtClock(piece.end_offset_s)}
              {piece.ended_reason === 'manual' ? ' · ended early' : piece.ended_reason === 'session_end' ? ' · ended with session' : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <KV label="Distance" value={`${piece.distance_m ?? 0} m`} />
            <KV label="Time" value={fmtClock(piece.time_s)} />
            <KV label="Avg split /500m" value={formatSplitTenths(piece.avg_split_s)} />
            <KV label="Avg stroke rate" value={piece.avg_stroke_rate ?? '—'} />
            <KV label="Stroke count" value={piece.stroke_count ?? '—'} />
            {piece.target_pace_s ? <KV label="Target pace" value={formatSplitTenths(piece.target_pace_s)} /> : null}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default function SessionBreakdown({
  workout,
  formatters,
  onExport,
  stravaConnected = false,
  intervalsConnected = false,
}) {
  const children = Array.isArray(workout?.child_sessions) ? workout.child_sessions : [];

  // Build the per-strip export button. Child strips reuse the PARENT
  // workout because v1.6 child sessions don't have their own time-series
  // arrays yet — the file is always the full session.
  const buildExportBtn = (keySuffix) => onExport ? (
    <RowExportMenu
      workout={workout}
      onExport={onExport}
      stravaConnected={stravaConnected}
      intervalsConnected={intervalsConnected}
      testidPrefix={`child-export-${keySuffix}`}
      size="sm"
    />
  ) : null;

  // v1.6 native path — render every recorded child.
  if (children.length > 0) {
    return (
      <div className="space-y-2 mb-4" data-testid="session-breakdown">
        <div className="text-xs font-bold uppercase tracking-widest text-zinc-500 dark:text-zinc-400">
          Session breakdown · {children.length} {children.length === 1 ? 'piece' : 'pieces'}
        </div>
        {children.map((c, i) => {
          // Native v1.6 children carry their own per-piece stats — do NOT
          // fall back to parent aggregates or the strip lies (parent avg
          // split leaking into every child was iter-63 issue #3).
          const child = c;
          const btn = buildExportBtn(`${workout.workout_id || 'w'}-${i}`);
          if (c.type === 'piece')    return <PieceChild key={c.piece_id || i} piece={c} />;
          if (c.type === 'race')     return <RaceChild key={i} workout={child} formatters={formatters} exportBtn={btn} />;
          if (c.type === 'program')  return <ProgramChild key={i} workout={child} formatters={formatters} exportBtn={btn} />;
          if (c.type === 'distance') return <DistanceChild key={i} workout={child} formatters={formatters} exportBtn={btn} />;
          return <StandardChild key={i} workout={child} formatters={formatters} exportBtn={btn} />;
        })}
      </div>
    );
  }

  // Legacy compatibility read layer — infer a single synthetic child.
  const sessionType = workout?.session_type
    || (workout?.program_id || workout?.program_name ? 'program'
        : (Array.isArray(workout?.splits_500m) && workout.splits_500m.length > 0) ? 'race'
        : workout?.target_distance ? 'distance'
        : 'standard');

  const Child =
    sessionType === 'race' ? RaceChild
    : sessionType === 'program' ? ProgramChild
    : sessionType === 'distance' ? DistanceChild
    : StandardChild;

  return (
    <div className="mb-4" data-testid="session-breakdown">
      <div className="text-xs font-bold uppercase tracking-widest text-zinc-500 dark:text-zinc-400 mb-2">
        Session breakdown
      </div>
      <Child workout={workout} formatters={formatters} exportBtn={buildExportBtn(workout.workout_id || 'legacy')} />
    </div>
  );
}
